import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Badge } from './ui/badge';
import { Switch } from './ui/switch';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger } from './ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Textarea } from './ui/textarea';
import { toast } from 'sonner';
import axios from 'axios';
import {
  UserX,
  UserCheck,
  AlertTriangle,
  Mail,
  Clock,
  Settings,
  Search,
  Send,
  Ban,
  CheckCircle,
  Loader2,
  History,
  TrendingUp,
  Users,
  CreditCard,
  RefreshCw
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const SuspensionManager = ({ token }) => {
  const [loading, setLoading] = useState(true);
  const [config, setConfig] = useState(null);
  const [overdueUsers, setOverdueUsers] = useState([]);
  const [stats, setStats] = useState(null);
  const [selectedUser, setSelectedUser] = useState(null);
  const [showSuspendDialog, setShowSuspendDialog] = useState(false);
  const [showHistoryDialog, setShowHistoryDialog] = useState(false);
  const [suspendReason, setSuspendReason] = useState('');
  const [userHistory, setUserHistory] = useState(null);
  const [daysOverdue, setDaysOverdue] = useState(30);
  const [userType, setUserType] = useState('all');

  const headers = { Authorization: `Bearer ${token}` };

  const fetchData = async () => {
    setLoading(true);
    try {
      const [configRes, overdueRes, statsRes] = await Promise.all([
        axios.get(`${API_URL}/api/suspension/config`, { headers }),
        axios.get(`${API_URL}/api/suspension/overdue-payments?days_overdue=${daysOverdue}${userType !== 'all' ? `&user_type=${userType}` : ''}`, { headers }),
        axios.get(`${API_URL}/api/suspension/stats`, { headers })
      ]);
      setConfig(configRes.data);
      setOverdueUsers(overdueRes.data.users || []);
      setStats(statsRes.data);
    } catch (error) {
      console.error('Error:', error);
      toast.error('Erreur lors du chargement');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchData(); }, [daysOverdue, userType]);

  const updateConfig = async (updates) => {
    try {
      await axios.put(`${API_URL}/api/suspension/config`, { ...config, ...updates }, { headers });
      setConfig({ ...config, ...updates });
      toast.success('Configuration mise à jour');
    } catch (error) {
      toast.error('Erreur');
    }
  };

  const sendReminder = async (userId) => {
    try {
      await axios.post(`${API_URL}/api/suspension/send-reminder/${userId}`, {}, { headers });
      toast.success('Relance envoyée');
      fetchData();
    } catch (error) {
      toast.error('Erreur lors de l\'envoi');
    }
  };

  const sendBulkReminders = async () => {
    try {
      const res = await axios.post(`${API_URL}/api/suspension/send-bulk-reminders`, {}, { headers });
      toast.success(`${res.data.reminders_sent} relances envoyées`);
      fetchData();
    } catch (error) {
      toast.error('Erreur');
    }
  };

  const suspendUser = async () => {
    if (!selectedUser || !suspendReason) {
      toast.error('Raison requise');
      return;
    }
    try {
      await axios.post(`${API_URL}/api/suspension/suspend`, {
        user_id: selectedUser.id,
        reason: suspendReason,
        notify_user: true
      }, { headers });
      toast.success('Utilisateur suspendu');
      setShowSuspendDialog(false);
      setSuspendReason('');
      fetchData();
    } catch (error) {
      toast.error('Erreur');
    }
  };

  const reinstateUser = async (userId) => {
    try {
      await axios.post(`${API_URL}/api/suspension/reinstate`, {
        user_id: userId,
        note: 'Réactivé par admin'
      }, { headers });
      toast.success('Utilisateur réactivé');
      fetchData();
    } catch (error) {
      toast.error('Erreur');
    }
  };

  const viewHistory = async (user) => {
    setSelectedUser(user);
    try {
      const res = await axios.get(`${API_URL}/api/suspension/history/${user.id}`, { headers });
      setUserHistory(res.data);
      setShowHistoryDialog(true);
    } catch (error) {
      toast.error('Erreur');
    }
  };

  if (loading) {
    return <div className="flex justify-center p-8"><Loader2 className="w-6 h-6 animate-spin" /></div>;
  }

  return (
    <div className="space-y-6" data-testid="suspension-manager">
      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card className="bg-gradient-to-br from-red-500 to-red-600 text-white">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <UserX className="w-8 h-8 opacity-80" />
              <div>
                <p className="text-red-100 text-sm">Suspendus</p>
                <p className="text-2xl font-bold">{stats?.suspended_users || 0}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card className="bg-gradient-to-br from-amber-500 to-amber-600 text-white">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <AlertTriangle className="w-8 h-8 opacity-80" />
              <div>
                <p className="text-amber-100 text-sm">Impayés</p>
                <p className="text-2xl font-bold">{stats?.overdue_users || 0}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card className="bg-gradient-to-br from-blue-500 to-blue-600 text-white">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <TrendingUp className="w-8 h-8 opacity-80" />
              <div>
                <p className="text-blue-100 text-sm">Suspensions/mois</p>
                <p className="text-2xl font-bold">{stats?.this_month?.suspensions || 0}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card className="bg-gradient-to-br from-green-500 to-green-600 text-white">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <UserCheck className="w-8 h-8 opacity-80" />
              <div>
                <p className="text-green-100 text-sm">Réactivations/mois</p>
                <p className="text-2xl font-bold">{stats?.this_month?.reinstatements || 0}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Configuration */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Settings className="w-5 h-5" />
            Configuration de la Politique de Suspension
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
            <div>
              <p className="font-medium">Système actif</p>
              <p className="text-sm text-muted-foreground">Active les relances et suspensions automatiques</p>
            </div>
            <Switch
              checked={config?.enabled || false}
              onCheckedChange={(checked) => updateConfig({ enabled: checked })}
            />
          </div>
          
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div>
              <Label>Délai de grâce (jours)</Label>
              <Input
                type="number"
                value={config?.grace_period_days || 7}
                onChange={(e) => updateConfig({ grace_period_days: parseInt(e.target.value) })}
              />
            </div>
            <div>
              <Label>Nombre de relances</Label>
              <Input
                type="number"
                value={config?.reminder_count || 3}
                onChange={(e) => updateConfig({ reminder_count: parseInt(e.target.value) })}
              />
            </div>
            <div>
              <Label>Intervalle (jours)</Label>
              <Input
                type="number"
                value={config?.reminder_interval_days || 7}
                onChange={(e) => updateConfig({ reminder_interval_days: parseInt(e.target.value) })}
              />
            </div>
            <div className="flex items-end">
              <div className="flex items-center gap-2">
                <Switch
                  checked={config?.auto_suspend || false}
                  onCheckedChange={(checked) => updateConfig({ auto_suspend: checked })}
                />
                <Label>Auto-suspension</Label>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Overdue Users List */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="flex items-center gap-2">
              <CreditCard className="w-5 h-5" />
              Utilisateurs avec Paiements en Retard
            </CardTitle>
            <div className="flex items-center gap-3">
              <Select value={userType} onValueChange={setUserType}>
                <SelectTrigger className="w-40">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Tous</SelectItem>
                  <SelectItem value="patient">Patients</SelectItem>
                  <SelectItem value="professional">Professionnels</SelectItem>
                </SelectContent>
              </Select>
              <Input
                type="number"
                value={daysOverdue}
                onChange={(e) => setDaysOverdue(parseInt(e.target.value))}
                className="w-20"
              />
              <span className="text-sm text-muted-foreground">jours</span>
              <Button variant="outline" onClick={fetchData}>
                <RefreshCw className="w-4 h-4" />
              </Button>
              <Button onClick={sendBulkReminders}>
                <Send className="w-4 h-4 mr-2" />
                Relancer tous
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {overdueUsers.length === 0 ? (
            <p className="text-center text-muted-foreground py-8">Aucun utilisateur en retard de paiement</p>
          ) : (
            <div className="space-y-3">
              {overdueUsers.map((user) => (
                <div key={user.id} className={`p-4 rounded-lg border ${user.is_suspended ? 'bg-red-50 border-red-200' : 'bg-white'}`}>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-4">
                      <div>
                        <div className="flex items-center gap-2">
                          <p className="font-medium">{user.first_name} {user.last_name}</p>
                          <Badge variant={user.role === 'professional' ? 'default' : 'secondary'}>
                            {user.role === 'professional' ? 'Pro' : 'Patient'}
                          </Badge>
                          {user.is_suspended && <Badge variant="destructive">Suspendu</Badge>}
                        </div>
                        <p className="text-sm text-muted-foreground">{user.email}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-4">
                      <div className="text-right">
                        <p className="text-sm">Relances: <span className="font-bold">{user.reminders_sent}</span></p>
                        <p className="text-xs text-muted-foreground">
                          {user.last_reminder ? `Dernière: ${new Date(user.last_reminder).toLocaleDateString()}` : 'Jamais'}
                        </p>
                      </div>
                      <div className="flex gap-2">
                        <Button variant="outline" size="sm" onClick={() => viewHistory(user)}>
                          <History className="w-4 h-4" />
                        </Button>
                        {!user.is_suspended ? (
                          <>
                            <Button variant="outline" size="sm" onClick={() => sendReminder(user.id)}>
                              <Mail className="w-4 h-4 mr-1" />
                              Relancer
                            </Button>
                            <Button 
                              variant="destructive" 
                              size="sm"
                              onClick={() => { setSelectedUser(user); setShowSuspendDialog(true); }}
                            >
                              <Ban className="w-4 h-4 mr-1" />
                              Suspendre
                            </Button>
                          </>
                        ) : (
                          <Button variant="outline" size="sm" className="text-green-600" onClick={() => reinstateUser(user.id)}>
                            <CheckCircle className="w-4 h-4 mr-1" />
                            Réactiver
                          </Button>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Suspend Dialog */}
      <Dialog open={showSuspendDialog} onOpenChange={setShowSuspendDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Suspendre l'utilisateur</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <p>Vous allez suspendre <strong>{selectedUser?.email}</strong></p>
            <div>
              <Label>Raison de la suspension</Label>
              <Textarea
                value={suspendReason}
                onChange={(e) => setSuspendReason(e.target.value)}
                placeholder="Ex: Paiement non régularisé après 3 relances"
                rows={3}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowSuspendDialog(false)}>Annuler</Button>
            <Button variant="destructive" onClick={suspendUser}>Confirmer la suspension</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* History Dialog */}
      <Dialog open={showHistoryDialog} onOpenChange={setShowHistoryDialog}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Historique - {selectedUser?.email}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4 max-h-96 overflow-y-auto">
            <div>
              <h4 className="font-medium mb-2">Relances envoyées</h4>
              {userHistory?.payment_reminders?.length > 0 ? (
                <div className="space-y-2">
                  {userHistory.payment_reminders.map((r, i) => (
                    <div key={i} className="p-2 bg-amber-50 rounded text-sm">
                      Relance #{r.reminder_number} - {new Date(r.sent_at).toLocaleString()}
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-sm text-muted-foreground">Aucune relance</p>
              )}
            </div>
            <div>
              <h4 className="font-medium mb-2">Actions de suspension</h4>
              {userHistory?.suspension_actions?.length > 0 ? (
                <div className="space-y-2">
                  {userHistory.suspension_actions.map((a, i) => (
                    <div key={i} className={`p-2 rounded text-sm ${a.action === 'suspended' ? 'bg-red-50' : 'bg-green-50'}`}>
                      {a.action === 'suspended' ? '🔒 Suspendu' : '✅ Réactivé'} - {new Date(a.timestamp).toLocaleString()}
                      {a.reason && <p className="text-xs text-muted-foreground mt-1">{a.reason}</p>}
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-sm text-muted-foreground">Aucune action</p>
              )}
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default SuspensionManager;
